import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Box, Stack, SwipeableDrawer, styled, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';
import { useEffect } from 'react';

import { PortalTheme } from '@frontend-libs/portal';

export interface BottomDrawerProps {
  openState: boolean;
  toggleDrawer(state: boolean): void;
  onOpenTransitionFinished?(): void;
  onCloseTransitionFinished?(): void;
  content: React.ReactNode;
  footer?: React.ReactNode;
  headerBackgroundColor?: string;
  disablePadding?: boolean;
}

const Puller = styled('div')(({ theme }) => ({
  width: 50,
  height: 6,
  backgroundColor: theme.palette.grey[300],
  borderRadius: 3,
}));

export const BottomDrawer = (props: BottomDrawerProps) => {
  const theme = useTheme<PortalTheme>();

  useEffect(() => {
    if (props.openState && Capacitor.getPlatform() === 'android') {
      const listener = App.addListener('backButton', () => {
        props.toggleDrawer(false);
      });

      return () => {
        listener
          .then((handle) => handle.remove())
          .catch((error) => {
            console.error(error);
            captureException(error);
          });
      };
    }

    return undefined;
  }, [props.openState, props.toggleDrawer]);

  return (
    <SwipeableDrawer
      open={props.openState}
      onOpen={() => {
        props.toggleDrawer(true);
      }}
      onClose={() => {
        props.toggleDrawer(false);
      }}
      onTransitionEnd={() => {
        if (props.openState) {
          props.onOpenTransitionFinished?.();
        } else {
          props.onCloseTransitionFinished?.();
        }
      }}
      id="drawer-bottom"
      slotProps={{
        paper: {
          sx: {
            width: '100%',
            height: '100%',
            maxHeight: '90%',
            borderTopRightRadius: theme.borderRadiusLarge,
            borderTopLeftRadius: theme.borderRadiusLarge,
            [theme.breakpoints.up('sm')]: {
              margin: 'auto',
              maxWidth: theme.contentMaxWidth,
            },
          },
        },
      }}
      anchor="bottom"
      disableSwipeToOpen
      disableDiscovery={true}
    >
      <Stack
        py={2}
        justifyContent="center"
        alignItems="center"
        position="sticky"
        sx={{ backgroundColor: props.headerBackgroundColor }}
      >
        <Puller />
      </Stack>

      <Stack
        p={props.disablePadding ? 0 : 2}
        sx={{
          paddingBottom: props.footer
            ? `calc(var(--safe-area-inset-bottom) + ${theme.spacing(13)})`
            : `calc(var(--safe-area-inset-bottom) + ${theme.spacing(2)})`,
        }}
        overflow="auto"
        ref={(el) => el?.scrollTo(0, 0)}
      >
        {props.content}
      </Stack>
      {props.footer && (
        <Stack
          justifyContent="center"
          alignItems="center"
          position="fixed"
          width="100%"
          bottom={0}
          sx={{
            backgroundColor: theme.palette.background.paper,
            height: theme.spacing(11),
            pb: `var(--safe-area-inset-bottom)`,
          }}
        >
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              p: 2,
              pb: 3,
              width: '100%',
              height: '100%',
              position: 'sticky',
            }}
          >
            {props.footer}
          </Box>
        </Stack>
      )}
    </SwipeableDrawer>
  );
};
